// Analytics helpers for Netflix "Viewing Activity" exports.
// The CSV Netflix gives you has two columns: `Title` and `Date`.
// Titles for episodes look like "Show: Season 1: Episode Name" while
// movies are usually a bare title with no colon, so we use the first
// segment before ":" as the series name and treat colon-less rows as movies.

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const WEEKDAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Netflix's export tells us *what* was watched, not for how long, so we
// estimate runtime from typical averages. These are rough blends across
// genres and are surfaced in the UI so it's clear they're estimates.
export const AVG_EPISODE_MIN = 35;
export const AVG_MOVIE_MIN = 106;

// Meteorological seasons (Northern hemisphere). Month index (0-11) -> season.
const SEASON_NAMES = ["Winter", "Spring", "Summer", "Autumn"];
const SEASON_OF_MONTH = [0, 0, 1, 1, 1, 2, 2, 2, 3, 3, 3, 0]; // Jan..Dec

const FULL_MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Netflix exports dates as M/D/YY (US format). Fall back to Date parsing
// for anything unexpected. Returns a valid Date or null.
export function parseNetflixDate(raw) {
  if (!raw) return null;
  const value = String(raw).trim();
  if (!value) return null;

  const parts = value.split("/");
  if (parts.length === 3) {
    let [month, day, year] = parts.map((p) => parseInt(p, 10));
    if (!Number.isNaN(month) && !Number.isNaN(day) && !Number.isNaN(year)) {
      if (year < 100) year += year < 70 ? 2000 : 1900;
      const d = new Date(year, month - 1, day);
      return Number.isNaN(d.getTime()) ? null : d;
    }
  }

  const fallback = new Date(value);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

export function seriesName(title) {
  if (!title) return "Unknown";
  const idx = title.indexOf(":");
  return (idx === -1 ? title : title.slice(0, idx)).trim();
}

export function isMovie(title) {
  return typeof title === "string" && !title.includes(":");
}

const monthKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

const dayKey = (d) => `${monthKey(d)}-${String(d.getDate()).padStart(2, "0")}`;

const prettyMonth = (key) => {
  const [year, month] = key.split("-").map(Number);
  return `${MONTH_NAMES[month - 1]} ${year}`;
};

// Longest run of consecutive calendar days that each had at least one view.
function longestStreak(dayKeys) {
  const days = [...new Set(dayKeys)]
    .map((k) => new Date(k).getTime())
    .sort((a, b) => a - b);
  if (days.length === 0) return 0;

  const ONE_DAY = 24 * 60 * 60 * 1000;
  let best = 1;
  let current = 1;
  for (let i = 1; i < days.length; i++) {
    const gap = Math.round((days[i] - days[i - 1]) / ONE_DAY);
    current = gap === 1 ? current + 1 : 1;
    if (current > best) best = current;
  }
  return best;
}

// Turn raw parsed CSV rows into everything the UI needs. `price` is the
// monthly subscription cost used for the cost-per-view figures.
export function computeAnalytics(rows, price) {
  const items = [];
  for (const row of rows || []) {
    const title = row.Title ?? row.title;
    const date = parseNetflixDate(row.Date ?? row.date);
    if (title && date) items.push({ title, date });
  }

  if (items.length === 0) return null;

  items.sort((a, b) => a.date - b.date);

  const perMonth = new Map();      // monthKey -> count
  const perMonthMin = new Map();   // monthKey -> estimated minutes watched
  const perWeekday = new Array(7).fill(0);
  const perSeries = new Map();     // series -> count
  const perDay = new Map();        // dayKey -> count
  const perMonthOfYear = new Array(12).fill(0);  // Jan..Dec counts (all years)
  const seasonMinutes = new Array(4).fill(0);    // Winter,Spring,Summer,Autumn
  let movieCount = 0;

  for (const { title, date } of items) {
    const mKey = monthKey(date);
    const minutes = isMovie(title) ? AVG_MOVIE_MIN : AVG_EPISODE_MIN;
    perMonth.set(mKey, (perMonth.get(mKey) || 0) + 1);
    perMonthMin.set(mKey, (perMonthMin.get(mKey) || 0) + minutes);
    perWeekday[date.getDay()] += 1;
    perDay.set(dayKey(date), (perDay.get(dayKey(date)) || 0) + 1);
    perMonthOfYear[date.getMonth()] += 1;
    seasonMinutes[SEASON_OF_MONTH[date.getMonth()]] += minutes;

    const series = seriesName(title);
    perSeries.set(series, (perSeries.get(series) || 0) + 1);
    if (isMovie(title)) movieCount += 1;
  }

  const monthKeys = [...perMonth.keys()].sort();

  // Top series by view count.
  const topSeries = [...perSeries.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10);

  // Busiest single day.
  let busiestDay = { key: null, count: 0 };
  for (const [k, count] of perDay.entries()) {
    if (count > busiestDay.count) busiestDay = { key: k, count };
  }

  const first = items[0].date;
  const last = items[items.length - 1].date;
  const spanDays = Math.max(
    1,
    Math.round((last - first) / (24 * 60 * 60 * 1000)) + 1
  );

  const totalViews = items.length;
  const episodeCount = totalViews - movieCount;
  const activeMonths = monthKeys.length;
  const totalSpent = activeMonths * price;

  // Estimated time watched, derived from typical runtimes.
  const estMinutes = episodeCount * AVG_EPISODE_MIN + movieCount * AVG_MOVIE_MIN;
  const estHours = estMinutes / 60;
  const estFullDays = estHours / 24;

  // THE headline metric for "is it worth it": cost per hour watched.
  const costPerHour = estHours ? totalSpent / estHours : 0;

  // Dense month-by-month timeline including months with ZERO views, so the
  // charts and the "wasted subscription" figure reflect gaps honestly.
  const denseKeys = monthSpan(first, last);
  const denseTotals = denseKeys.map((k) => perMonth.get(k) || 0);
  const denseMinutes = denseKeys.map((k) => perMonthMin.get(k) || 0);
  let denseRun = 0;
  const denseCumulative = denseTotals.map((n) => (denseRun += n));
  const denseCostPerHour = denseMinutes.map((min) =>
    min ? price / (min / 60) : null
  );

  // Months you paid for but didn't watch anything — pure wasted spend.
  const spanMonths = denseKeys.length;
  const idleMonths = denseTotals.filter((n) => n === 0).length;
  const wastedSpend = idleMonths * price;

  // Usage trend: average plays/month in the recent half vs. the earlier half.
  const half = Math.floor(denseTotals.length / 2);
  const mean = (arr) =>
    arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;
  const earlyAvg = mean(denseTotals.slice(0, half));
  const recentAvg = mean(denseTotals.slice(half));
  const trendPct = earlyAvg ? ((recentAvg - earlyAvg) / earlyAvg) * 100 : 0;
  const trendDir =
    Math.abs(trendPct) < 10 ? "flat" : trendPct > 0 ? "up" : "down";

  // Seasonality: which season and which calendar month you binge most.
  let topSeasonIdx = 0;
  for (let i = 1; i < 4; i++) {
    if (seasonMinutes[i] > seasonMinutes[topSeasonIdx]) topSeasonIdx = i;
  }
  const topMonthIdx = perMonthOfYear.indexOf(Math.max(...perMonthOfYear));

  const verdict = buildVerdict({
    costPerHour,
    idleMonths,
    spanMonths,
    estHours,
    trendDir,
    trendPct,
    topSeason: SEASON_NAMES[topSeasonIdx],
  });

  return {
    // headline numbers for the summary cards
    summary: {
      totalViews,
      uniqueSeries: perSeries.size,
      movieCount,
      episodeCount,
      activeMonths,
      daysWatched: perDay.size,
      avgPerActiveDay: totalViews / perDay.size,
      avgPerMonth: totalViews / activeMonths,
      longestStreak: longestStreak([...perDay.keys()]),
      busiestDay:
        busiestDay.key &&
        `${new Date(busiestDay.key).toLocaleDateString()} (${busiestDay.count})`,
      firstDate: first.toLocaleDateString(),
      lastDate: last.toLocaleDateString(),
      spanDays,
      totalSpent,
      costPerView: totalSpent / totalViews,
      estMinutes,
      estHours,
      estFullDays,
      avgMinutesPerActiveDay: estMinutes / perDay.size,
      // value / worth-it signals
      costPerHour,
      spanMonths,
      idleMonths,
      wastedSpend,
      trendDir,
      trendPct,
      topSeason: SEASON_NAMES[topSeasonIdx],
      topMonth: FULL_MONTH_NAMES[topMonthIdx],
    },
    verdict,
    monthly: {
      labels: denseKeys.map(prettyMonth),
      totals: denseTotals,
      cumulative: denseCumulative,
      costPerView: denseTotals.map((n) => (n ? price / n : null)),
      costPerHour: denseCostPerHour,
    },
    weekday: {
      labels: WEEKDAY_NAMES,
      totals: perWeekday,
    },
    season: {
      labels: SEASON_NAMES,
      hours: seasonMinutes.map((m) => +(m / 60).toFixed(1)),
    },
    monthOfYear: {
      labels: ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"],
      totals: perMonthOfYear,
    },
    topSeries: {
      labels: topSeries.map(([name]) => name),
      totals: topSeries.map(([, count]) => count),
    },
  };
}

// Build the plain-English "is Netflix worth it?" verdict. Tone drives the
// banner colour: great/good = green, ok = amber, bad = red.
function buildVerdict({ costPerHour, idleMonths, spanMonths, estHours, trendDir, trendPct, topSeason }) {
  let tone;
  let headline;
  if (estHours < 2) {
    tone = "bad";
    headline = "You've barely watched — hard to justify the cost";
  } else if (costPerHour <= 1) {
    tone = "great";
    headline = "Great value — you get a lot for what you pay";
  } else if (costPerHour <= 3) {
    tone = "good";
    headline = "Worth it — a fair price for how much you watch";
  } else if (costPerHour <= 6) {
    tone = "ok";
    headline = "Borderline — you're paying a premium per hour";
  } else {
    tone = "bad";
    headline = "Probably not worth it — very high cost per hour";
  }

  // A lot of idle months is a strong "pause it" signal regardless of rate.
  const idleShare = spanMonths ? idleMonths / spanMonths : 0;
  if (idleShare >= 0.4 && tone !== "bad") {
    tone = "ok";
    headline = "Consider pausing — you skip Netflix many months";
  }

  const points = [];
  points.push(`You pay about $${costPerHour.toFixed(2)} per hour watched.`);
  if (idleMonths > 0) {
    points.push(
      `${idleMonths} of ${spanMonths} months you paid but watched nothing.`
    );
  }
  if (trendDir === "down") {
    points.push(`Your watching is trending down (${Math.round(Math.abs(trendPct))}% lower lately).`);
  } else if (trendDir === "up") {
    points.push(`Your watching is trending up (${Math.round(trendPct)}% higher lately).`);
  }
  points.push(`You watch most in ${topSeason}.`);

  return { tone, headline, points };
}

// List of "YYYY-MM" keys from first to last month inclusive (dense timeline).
function monthSpan(first, last) {
  const keys = [];
  let year = first.getFullYear();
  let month = first.getMonth();
  const lastYear = last.getFullYear();
  const lastMonth = last.getMonth();
  while (year < lastYear || (year === lastYear && month <= lastMonth)) {
    keys.push(`${year}-${String(month + 1).padStart(2, "0")}`);
    month += 1;
    if (month > 11) {
      month = 0;
      year += 1;
    }
  }
  return keys;
}
